import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationExtras, Router } from '@angular/router';

interface FlattenedRouteState {
  config: string[],
  url: string[]
}

@Injectable({
  providedIn: 'root',
})
export class RoutingHelperService {

  constructor(
    private router: Router,
  ) {}


  /**
   * Navigates to the current route with the passed paramater change to the new value
   *
   * if paramater is not part of current route then it appends the value to the current route
   * @param paramToChange name of the paramater to change
   * @param newParamValue new value for that paramater
   * @returns void
   */
  public changeParam(paramToChange: string, newParamValue: string, navigationExtras?: NavigationExtras): void {

    const flattenedConfig = this.flattenRouteTree(this.router.routerState.root.firstChild.snapshot);
    const targetRouteIndex = flattenedConfig.config.findIndex(routeConfigSegment => routeConfigSegment === ':' + paramToChange);

    // couldn't find param
    if (targetRouteIndex === -1) {
      // just append the new param
      this.router.navigate([...flattenedConfig.url, newParamValue], { queryParamsHandling: 'preserve' });
      return;
    }

    const newRoute = flattenedConfig.url.map((segment, i) => i === targetRouteIndex ? newParamValue : segment);
    this.router.navigate(newRoute, { queryParamsHandling: 'preserve', ...navigationExtras });
  }

  /**
   * Applies a query param object preserving other params in the route
   *
   * @param queryParams object with values to change
   * @returns void
   */
  public setQueryParams(queryParams: Record<string, string|number>): void {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Recurses the route tree to get all the route parameters
   *
   * @returns Record<string, string>
   */
  public getGlobalRouteParams(): Record<string, string> {
    const buildParams = (route: ActivatedRoute): Record<string, string> => {
      let params = route.snapshot.params;
      if (route.firstChild) {
        params = { ...params, ...buildParams(route.firstChild) };
      }
      return params;
    };
    return buildParams(this.router.routerState.root);
  }

  private flattenRouteTree(currentRoute: ActivatedRouteSnapshot): FlattenedRouteState {

    const parsedRouteConfig = currentRoute.routeConfig.path.split('/').filter(segment => segment !== '');
    const parsedUrlSegments = currentRoute.url.map(segment => segment.path);

    if (!currentRoute.firstChild) {
      return {
        config: parsedRouteConfig,
        url: parsedUrlSegments,
      };
    }

    const furtherChildren = this.flattenRouteTree(currentRoute.firstChild);

    return {
      config: [...parsedRouteConfig, ...furtherChildren.config],
      url: [...parsedUrlSegments, ...furtherChildren.url],
    };
  }
}
